"use client";

import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { useAccount, useConnect, useConnectors } from "wagmi";
import { useWallet as useSolanaWallet } from "@solana/wallet-adapter-react";
import { useWallet as useAptosWallet } from "@aptos-labs/wallet-adapter-react";
import { useSignIn } from "@/lib/useSignIn";
import { hasWalletConnect } from "@/lib/wallet-config";
import type { ChainFamily } from "@/lib/auth-api";

// The wagmi walletConnect connector exposes no `.icon`, so the row rendered blank. Inline the
// WalletConnect mark as a data URI (no network fetch, works offline / behind the CSP).
const WALLETCONNECT_ICON =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg"><rect width="28" height="28" rx="8" fill="#3396FF"/><path d="M8.4 10.9c3.1-3 8.1-3 11.2 0l.37.36c.15.15.15.4 0 .55l-1.27 1.24c-.08.08-.2.08-.28 0l-.51-.5c-2.16-2.12-5.66-2.12-7.82 0l-.55.54c-.08.08-.2.08-.28 0L7.99 11.85c-.15-.15-.15-.4 0-.55l.41-.4zm13.83 2.58l1.13 1.1c.15.15.15.4 0 .55l-5.11 5.01c-.15.15-.4.15-.56 0l-3.63-3.56a.1.1 0 00-.14 0l-3.63 3.56c-.16.15-.41.15-.56 0L4.63 15.13c-.15-.15-.15-.4 0-.55l1.13-1.1c.16-.16.41-.16.56 0l3.63 3.55c.04.04.1.04.14 0l3.63-3.55c.15-.16.4-.16.56 0l3.63 3.55c.04.04.1.04.14 0l3.63-3.55c.16-.16.41-.16.56 0z" fill="#fff"/></svg>`
  );

// Fallback for the "sign in" row when the connected wallet exposes no icon. A pen/signature
// mark, since that step is about signing a message. Better than a blank square.
const SIGN_ICON =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="%2314a03a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>`
  );

const TABS: { id: ChainFamily; label: string }[] = [
  { id: "evm", label: "EVM" },
  { id: "solana", label: "Solana" },
  { id: "aptos", label: "Aptos" },
];

const Row = ({
  name,
  icon,
  onClick,
  disabled,
  hint,
}: {
  name: string;
  icon?: string;
  onClick: () => void;
  disabled?: boolean;
  hint?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="flex w-full items-center gap-3 rounded-md border border-line bg-raised px-3 py-2.5 text-left transition-colors hover:border-brand disabled:cursor-not-allowed disabled:opacity-50"
  >
    {icon ? (
      // Wallet icons arrive from the adapters at runtime as data: URIs or arbitrary remote
      // hosts. next/image requires hosts to be whitelisted up front, so it can't serve them.
      // eslint-disable-next-line @next/next/no-img-element
      <img src={icon} alt="" width={20} height={20} className="h-5 w-5 rounded" />
    ) : (
      <span className="h-5 w-5 rounded bg-surface" />
    )}
    <span className="flex-1 text-sm text-fg">{name}</span>
    {hint && <span className="text-[11px] text-fg-muted">{hint}</span>}
  </button>
);

const ConnectModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [tab, setTab] = useState<ChainFamily>("evm");
  // Errors thrown DURING connect (before sign-in), e.g. a missing WC dependency, or the user
  // dismissing the wallet. These used to be swallowed, which is exactly why WalletConnect
  // "did nothing" instead of reporting a real failure.
  const [connectError, setConnectError] = useState<string | null>(null);
  const { signIn, isSigningIn, error, clearError } = useSignIn();

  const connectors = useConnectors();
  const { connectAsync } = useConnect();
  const { isConnected: evmConnected, address: evmAddress, connector: evmConnector } = useAccount();

  const solana = useSolanaWallet();
  const aptos = useAptosWallet();

  // When a wallet is already connected, the "sign in" row should show WHICH wallet/account is
  // about to sign, not a blank placeholder. Pull the connected wallet's icon + a short
  // address per ecosystem.
  const short = (a?: string) => (a && a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a);
  const signInRow = {
    evm: { icon: evmConnector?.icon, address: short(evmAddress) },
    solana: {
      icon: solana.wallet?.adapter.icon,
      address: short(solana.publicKey?.toBase58()),
    },
    aptos: {
      icon: aptos.wallet?.icon,
      address: short(aptos.account?.address?.toString()),
    },
  };

  useEffect(() => {
    if (!open || !panelRef.current) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.from(panelRef.current, { y: 12, opacity: 0, duration: 0.25, ease: "power2.out" });
    });
    return () => mm.revert();
  }, [open]);

  // Close on Escape, a modal that traps you is worse than no modal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const run = async (fn: () => Promise<unknown>) => {
    setConnectError(null);
    try {
      await fn();
      onClose();
    } catch (e) {
      const msg = (e as Error)?.message ?? "";
      // A user closing their wallet prompt isn't an error worth shouting about.
      if (/reject|denied|cancel|closed/i.test(msg)) return;
      // Everything else (a missing connector dependency, a relay failure) must be visible,
      // not silent. Silent failure is what made WalletConnect look broken.
      setConnectError(msg || "Couldn't connect. Please try again.");
    }
  };

  // Build the EVM wallet list, resolving the "Injected" confusion.
  //
  // wagmi v3 runs EIP-6963 discovery: each installed extension announces itself, so the
  // connector list holds real, named entries ("MetaMask", "Rabby", …) alongside the generic
  // `injected()` connector, whose name is the unhelpful literal "Injected". Showing both is
  // what surfaced a bare "Injected" row next to the actual wallet.
  //
  // So: prefer the discovered wallets (real name + icon). Fall back to the generic connector
  // ONLY when nothing was discovered, and label it "Browser Wallet", since "Injected" is
  // jargon to everyone who isn't a wallet developer.
  const walletConnectConnector = connectors.find((c) => c.id === "walletConnect");
  const genericInjected = connectors.find((c) => c.id === "injected");
  const discovered = connectors.filter(
    (c) => c.type === "injected" && c.id !== "injected"
  );

  const evmWallets: { key: string; name: string; icon?: string; connector: (typeof connectors)[number] }[] = [
    ...(discovered.length > 0
      ? discovered.map((c) => ({ key: c.uid, name: c.name, icon: c.icon, connector: c }))
      : genericInjected
        ? [{ key: genericInjected.uid, name: "Browser Wallet", icon: genericInjected.icon, connector: genericInjected }]
        : []),
    ...(walletConnectConnector
      ? [
          {
            key: walletConnectConnector.uid,
            name: "WalletConnect",
            icon: walletConnectConnector.icon ?? WALLETCONNECT_ICON,
            connector: walletConnectConnector,
          },
        ]
      : []),
  ];

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Connect a wallet"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-lg border border-line bg-surface p-4 shadow-2xl"
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-fg">Connect a wallet</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-fg-muted hover:text-fg"
          >
            ✕
          </button>
        </div>

        <p className="mb-3 text-xs text-fg-muted">
          Your wallet is your login. You&apos;ll sign a message to prove ownership. It costs
          no gas and sends no transaction.
        </p>

        <div className="mb-3 flex gap-1" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => {
                setTab(t.id);
                clearError();
                setConnectError(null);
              }}
              className={`flex-1 rounded px-2 py-1 text-xs transition-colors ${
                tab === t.id ? "bg-raised text-fg" : "text-fg-muted hover:text-fg"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          {tab === "evm" &&
            (evmConnected ? (
              <Row
                name={signInRow.evm.address ? `Sign in as ${signInRow.evm.address}` : "Sign in to continue"}
                icon={signInRow.evm.icon ?? SIGN_ICON}
                onClick={() => run(() => signIn("evm"))}
              />
            ) : (
              <>
                {evmWallets.map((w) => (
                  <Row
                    key={w.key}
                    name={w.name}
                    icon={w.icon}
                    onClick={() =>
                      run(async () => {
                        await connectAsync({ connector: w.connector });
                        await signIn("evm");
                      })
                    }
                  />
                ))}
                {evmWallets.length === 0 && (
                  <p className="px-1 py-3 text-xs text-fg-muted">
                    No EVM wallet detected. Install MetaMask or another browser wallet.
                  </p>
                )}
                {!hasWalletConnect && (
                  <p className="px-1 text-[11px] text-fg-muted">
                    WalletConnect (mobile/QR) is unavailable: no project ID configured at
                    build time.
                  </p>
                )}
              </>
            ))}

          {tab === "solana" &&
            (solana.connected ? (
              <Row
                name={signInRow.solana.address ? `Sign in as ${signInRow.solana.address}` : "Sign in to continue"}
                icon={signInRow.solana.icon ?? SIGN_ICON}
                onClick={() => run(() => signIn("solana"))}
              />
            ) : (
              <>
                {solana.wallets.map((w) => (
                  <Row
                    key={w.adapter.name}
                    name={w.adapter.name}
                    icon={w.adapter.icon}
                    hint={w.readyState === "Installed" ? undefined : "Not installed"}
                    disabled={w.readyState === "Unsupported"}
                    onClick={() =>
                      run(async () => {
                        solana.select(w.adapter.name);
                        await w.adapter.connect();
                        await signIn("solana");
                      })
                    }
                  />
                ))}
                {solana.wallets.length === 0 && (
                  <p className="px-1 py-3 text-xs text-fg-muted">
                    No Solana wallet detected. Install Phantom or Solflare.
                  </p>
                )}
              </>
            ))}

          {tab === "aptos" &&
            (aptos.connected ? (
              <Row
                name={signInRow.aptos.address ? `Sign in as ${signInRow.aptos.address}` : "Sign in to continue"}
                icon={signInRow.aptos.icon ?? SIGN_ICON}
                onClick={() => run(() => signIn("aptos"))}
              />
            ) : (
              <>
                {aptos.wallets?.map((w) => (
                  <Row
                    key={w.name}
                    name={w.name}
                    icon={w.icon}
                    onClick={() =>
                      run(async () => {
                        await aptos.connect(w.name);
                        await signIn("aptos");
                      })
                    }
                  />
                ))}
                {(aptos.wallets?.length ?? 0) === 0 && (
                  <p className="px-1 py-3 text-xs text-fg-muted">
                    No Aptos wallet detected. Install Petra, Pontem or Nightly.
                  </p>
                )}
              </>
            ))}
        </div>

        {isSigningIn && (
          <p className="mt-3 text-xs text-fg-muted">Waiting for signature…</p>
        )}
        {(error || connectError) && (
          <p className="mt-3 text-xs text-sell">{error || connectError}</p>
        )}
      </div>
    </div>
  );
};

export default ConnectModal;
